import { ComposableMap, Geographies, Geography, Marker, Line, ZoomableGroup } from "react-simple-maps";
import { orderCountries } from "../lib/tripStatus";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Flat interactive world map: highlighted countries, numbered pins in visit
// order, a dashed route line, and a distinct "currently here" marker.
export default function TripMap({ countries = [], origin = null, currentId = null, onSelectCountry = null }) {
  const ordered = orderCountries(countries);
  const stops = [];
  ordered.forEach((c) => {
    const cur = currentId ? c.id === currentId : c.is_current;
    const cityPins = (c.cities || []).filter((ct) => typeof ct.lat === "number" && typeof ct.lng === "number");
    if (cityPins.length) {
      cityPins.forEach((ct) => stops.push({ id: `${c.id || c.name}-${ct.name}`, countryId: c.id, lat: ct.lat, lng: ct.lng, cur }));
    } else if (typeof c.lat === "number" && typeof c.lng === "number") {
      stops.push({ id: c.id, countryId: c.id, lat: c.lat, lng: c.lng, cur });
    }
  });
  const pins = stops;

  const originPin = origin && typeof origin.lat === "number" && typeof origin.lng === "number" ? origin : null;
  const visited = new Set([...countries.map((c) => norm(nameOnly(c.name))), ...(originPin ? [norm(nameOnly(originPin.country))] : [])]);
  const routeCoords = [
    ...(originPin ? [[originPin.lng, originPin.lat]] : []),
    ...pins.map((c) => [c.lng, c.lat]),
  ];

  return (
    <div>
      <div data-testid="trip-map" className="w-full overflow-hidden rounded-2xl bg-[#EAF1F4]">
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 150 }}
          style={{ width: "100%", height: "auto" }}
        >
        <ZoomableGroup zoom={1} minZoom={1} maxZoom={8}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const isVisited = visited.has(norm(geo.properties.name));
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: { fill: isVisited ? "var(--tenant-accent)" : "#CBDBE3", stroke: "#EAF1F4", strokeWidth: 0.4, outline: "none" },
                      hover: { fill: isVisited ? "var(--tenant-accent)" : "#B9CDD8", outline: "none" },
                      pressed: { fill: "var(--tenant-accent)", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {routeCoords.length > 1 && (
            <Line
              coordinates={routeCoords}
              stroke="var(--brand-navy)"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeDasharray="4 4"
              fill="none"
            />
          )}

          {pins.map((c, idx) => {
            const isCur = c.cur;
            return (
            <Marker key={c.id || idx} coordinates={[c.lng, c.lat]}
              onClick={() => onSelectCountry && c.countryId && onSelectCountry(c.countryId)}
              style={{ default: { cursor: onSelectCountry ? "pointer" : "default" }, hover: { cursor: onSelectCountry ? "pointer" : "default" } }}>
              {isCur && (
                <circle r={11} fill="var(--tenant-accent)" opacity={0.25}>
                  <animate attributeName="r" from="7" to="16" dur="1.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.4" to="0" dur="1.6s" repeatCount="indefinite" />
                </circle>
              )}
              <circle r={7} fill={isCur ? "var(--tenant-accent)" : "var(--brand-navy)"} stroke="#fff" strokeWidth={1.5} />
              <text textAnchor="middle" y={2.8} fontSize={8} fontWeight="700" fill="#fff">{idx + 1}</text>
            </Marker>
          );})}

          {originPin && (
            <Marker data-testid="map-origin-marker" coordinates={[originPin.lng, originPin.lat]}>
              <circle r={9} fill="#2C2C3E" stroke="#fff" strokeWidth={1.6} />
              <path d="M -3.4 0.6 L 0 -3.2 L 3.4 0.6 L 3.4 4 L -3.4 4 Z" fill="#fff" />
            </Marker>
          )}
        </ZoomableGroup>
      </ComposableMap>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-[11px] text-muted-foreground flex-wrap">
        {originPin && <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#2C2C3E]" /> Home / departure</span>}
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: "var(--tenant-accent)" }} /> Countries you're visiting</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-[var(--tenant-accent)]/40" style={{ background: "var(--tenant-accent)" }} /> Currently here</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[var(--brand-navy)]" /> Planned stop</span>
      </div>
    </div>
  );
}

function nameOnly(name) {
  if (!name) return "";
  const parts = name.split(",");
  return parts[parts.length - 1].trim();
}
function norm(s) { return (s || "").replace(/[^\p{L}\s]/gu, "").trim().toLowerCase(); }
